<template>
  <div class="reconcile-panel">
    <!-- 头部：订单号 + 金额 + 状态（内联到详情抽屉时由 hero 承担，可隐藏） -->
    <div v-if="showHeader" class="recon-head">
      <div class="recon-head__amt">
        <span class="recon-head__amt-val">{{ money(orderInfo.payablePrice) }}</span>
        <span class="recon-head__order">{{ orderInfo.orderNumber || '-' }}</span>
      </div>
      <el-tag :type="isFailed ? 'danger' : 'warning'" size="small" effect="dark">
        {{ $t('order.userVerifyStatus2') }}
      </el-tag>
    </div>

    <!-- ① 判定横幅 -->
    <div :class="['verdict-banner', isFailed ? 'verdict-banner--crit' : 'verdict-banner--warn']">
      <i class="el-icon-warning verdict-banner__ic"></i>
      <div class="verdict-banner__text">
        <div>{{ verdictText }}</div>
        <div v-if="windowText" :class="['verdict-banner__window', withinWindow ? 'is-open' : 'is-closed']">
          <i :class="withinWindow ? 'el-icon-time' : 'el-icon-lock'"></i>
          {{ windowText }}
        </div>
      </div>
    </div>

    <!-- ② 开门证据 -->
    <div class="section-block" v-loading="videoLoading">
      <div class="section-block__title">
        <i class="el-icon-video-camera"></i>
        <span>{{ $t('order.openDoorVideo') }}</span>
      </div>
      <div class="video-wrap">
        <div
          v-for="video in videoList"
          :key="video.id"
          class="video-item"
          @click="previewVideo(video)"
        >
          <img
            v-if="videoPosterMap[video.resourceUrl]"
            :src="videoPosterMap[video.resourceUrl]"
            alt=""
            class="video-item__poster"
          />
          <video
            v-else
            class="video-item__player"
            :src="video.resourceUrl"
            muted
            playsinline
            preload="auto"
            @loadedmetadata="handleVideoThumbnailLoaded"
          ></video>
          <div class="video-item__icon">
            <i class="el-icon-video-play"></i>
          </div>
        </div>
        <el-empty v-if="!videoList.length" :description="$t('common.noData')"></el-empty>
      </div>
    </div>

    <!-- ③ 识别 vs 计费 -->
    <div class="section-block">
      <div class="section-block__header">
        <span class="section-block__title">
          <i class="el-icon-goods"></i>
          <span>{{ $t('order.detectedVsCharged') }}</span>
        </span>
        <el-button type="primary" plain size="mini" @click="showChooseProduct = true">
          {{ $t('order.chooseProducts') }}
        </el-button>
      </div>
      <div class="recon-lines">
        <div v-for="(item, index) in lineItems" :key="item.openDoorAisleGoodsId || item.goodsId || index" class="recon-line">
          <div class="recon-line__thumb">
            <img v-if="item.url" :src="item.url" alt="" />
            <i v-else class="el-icon-picture-outline"></i>
          </div>
          <div class="recon-line__main">
            <div class="recon-line__name" :title="item.goodsName">{{ item.goodsName || '-' }}</div>
            <div :class="['recon-line__meta', isMismatch(item) ? 'is-mismatch' : '']">
              {{ $t('order.confirmedShort') }}
              {{ item.detectedNum === null ? '—' : item.detectedNum }}
              · {{ $t('order.billedShort') }} {{ item.billedNum }}
              <span v-if="isMismatch(item)"> · {{ $t('order.mismatch') }}</span>
            </div>
          </div>
          <el-input-number
            v-model="item.qty"
            :min="0"
            :step="1"
            size="mini"
            controls-position="right"
            class="recon-line__qty"
          ></el-input-number>
          <div class="recon-line__price">{{ money(item.unitPrice * item.qty) }}</div>
          <i class="el-icon-close recon-line__rm" @click="removeLine(index)"></i>
        </div>
        <el-empty v-if="!lineItems.length" :description="$t('common.noData')"></el-empty>
      </div>
    </div>

    <!-- ④ 金额重算 -->
    <div class="recompute">
      <div class="recompute__line">
        <span>{{ $t('order.originalCharge') }}</span>
        <span class="recompute__num">{{ money(originalCharge) }}</span>
      </div>
      <div class="recompute__line">
        <span>{{ $t('order.correctedTotal') }}</span>
        <span class="recompute__num">{{ money(correctedTotal) }}</span>
      </div>
      <div class="recompute__line recompute__line--total">
        <span>{{ $t('order.customerDelta') }}</span>
        <span :class="['recompute__num', deltaClass]">{{ deltaText }}</span>
      </div>
      <div v-if="settlementAction === 'recharge'" class="recompute__note recompute__note--warn">
        <i class="el-icon-warning-outline"></i> {{ $t('order.rebillPending') }}
      </div>
      <div v-else-if="settlementAction === 'refund' && !withinWindow" class="recompute__note">
        <i class="el-icon-info"></i> {{ $t('order.refundHint') }}
      </div>
    </div>

    <!-- ⑤ 判定（approvalStatus：1 恶意 / 2 结算错误 / 3 正常） -->
    <div class="section-block section-block--compact">
      <div class="section-block__title">
        <i class="el-icon-collection-tag"></i>
        <span>{{ $t('order.resolution') }}</span>
      </div>
      <el-select
        v-model="form.approvalStatus"
        :placeholder="$t('order.approvalStatusPlaceholder')"
        size="small"
        style="width: 100%;"
      >
        <el-option
          v-for="item in normalizedApprovalOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
    </div>

    <!-- 视频预览 -->
    <el-dialog
      :visible.sync="videoPreviewVisible"
      :modal-append-to-body="true"
      :append-to-body="true"
      width="720px"
      :close-on-click-modal="false"
    >
      <span slot="title" class="dialog-title">
        <i class="el-icon-film"></i>
        <span>{{ $t('order.videoPreview') }}</span>
      </span>
      <video v-if="previewResourceUrl" :src="previewResourceUrl" controls width="100%"></video>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="videoPreviewVisible = false">{{ $t('common.cancel') }}</el-button>
      </span>
    </el-dialog>

    <!-- 选品器 -->
    <el-dialog
      :visible.sync="showChooseProduct"
      :modal-append-to-body="true"
      :append-to-body="true"
      custom-class="reconcile-panel__choose"
      width="960px"
      :close-on-click-modal="false"
      @open="loadProducts"
    >
      <span slot="title" class="dialog-title">
        <i class="el-icon-collection-tag"></i>
        <span>{{ $t('order.chooseProducts') }}</span>
      </span>
      <div class="product-selector">
        <div class="section-block section-block--compact">
          <div class="section-block__title">
            <i class="el-icon-box"></i>
            <span>{{ $t('order.availableProducts') }}</span>
          </div>
          <div v-loading="productLoading" class="product-selector__available">
            <div class="product-grid">
              <div
                v-for="item in productList"
                :key="item.openDoorAisleGoodsId"
                class="product-grid__item"
                @click="addProduct(item)"
              >
                <div class="product-grid__image">
                  <img :src="item.url" alt="" />
                </div>
                <div class="product-grid__name">{{ item.goodsName }}</div>
                <div class="product-grid__price">${{ item.sellingPrice }}</div>
              </div>
            </div>
            <el-empty v-if="!productLoading && !productList.length" :description="$t('common.noData')"></el-empty>
          </div>
        </div>

        <div class="section-block section-block--compact">
          <div class="section-block__title">
            <i class="el-icon-shopping-bag-1"></i>
            <span>{{ $t('order.selectedProducts') }}</span>
          </div>
          <div
            :class="[
              'product-selector__selected',
              { 'product-selector__selected--empty': !selectedProducts.length }
            ]"
          >
            <div
              v-for="item in selectedProducts"
              :key="item.openDoorAisleGoodsId"
              class="product-grid__item product-grid__item--selected"
            >
              <div class="product-grid__remove" @click="removeProduct(item)">
                <i class="el-icon-close"></i>
              </div>
              <div class="product-grid__image">
                <img :src="item.url" alt="" />
              </div>
              <div class="product-grid__name">{{ item.goodsName }}</div>
              <div class="product-grid__price">${{ item.sellingPrice }}</div>
              <el-input-number v-model="item.buyNum" :min="1" :step="1" size="small"></el-input-number>
            </div>
            <el-empty v-if="!selectedProducts.length" :description="$t('common.noData')"></el-empty>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="showChooseProduct = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" size="small" @click="confirmProducts">{{ $t('common.definite') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import orderStatus from "@/utils/orderStatus";

const SETTLEMENT_WINDOW_HOURS = 48; // Nayax 外部结算窗口（约 48–60h）
const EPS = 0.005;

export default {
  name: "ReconcilePanel",
  props: {
    orderInfo: {
      type: Object,
      default: () => ({})
    },
    approvalStatusOptions: {
      type: Array,
      default: () => []
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      videoLoading: false,
      productLoading: false,
      submitting: false,
      videoList: [],
      videoPosterMap: {},
      productList: [],
      lineItems: [],
      form: {
        approvalStatus: ""
      },
      showChooseProduct: false,
      selectedProducts: [],
      videoPreviewVisible: false,
      previewResourceUrl: "",
      nowTs: 0,
      windowTimer: null
    };
  },
  computed: {
    normalizedApprovalOptions() {
      if (this.approvalStatusOptions.length) {
        return this.approvalStatusOptions.map(item => ({
          value: Number(item.value !== undefined && item.value !== null ? item.value : item.dictValue),
          label: item.label || item.dictLabel
        }));
      }
      return [
        { value: 1, label: this.$t("order.approvalStatus1") },
        { value: 2, label: this.$t("order.approvalStatus2") },
        { value: 3, label: this.$t("order.approvalStatus3") }
      ];
    },
    isFailed() {
      return orderStatus.isRecognitionFailed(this.orderInfo.doorVerifyStatus);
    },
    shouldReviewProducts() {
      return [1, 2].includes(Number(this.form.approvalStatus));
    },
    originalCharge() {
      return this.round(Number(this.orderInfo.payablePrice) || 0);
    },
    correctedTotal() {
      const sum = this.lineItems.reduce((acc, item) => acc + (Number(item.unitPrice) || 0) * (Number(item.qty) || 0), 0);
      return this.round(sum);
    },
    delta() {
      return this.round(this.correctedTotal - this.originalCharge);
    },
    settlementAction() {
      if (this.delta < -EPS) {
        return "refund";
      }
      if (this.delta > EPS) {
        return "recharge";
      }
      return "none";
    },
    deltaClass() {
      if (this.settlementAction === "refund") {
        return "is-down";
      }
      if (this.settlementAction === "recharge") {
        return "is-up";
      }
      return "";
    },
    deltaText() {
      if (this.settlementAction === "none") {
        return this.$t("order.noChange");
      }
      const abs = this.money(Math.abs(this.delta));
      return this.settlementAction === "refund"
        ? `−${abs} ${this.$t("order.toRefund")}`
        : `+${abs} ${this.$t("order.toCharge")}`;
    },
    verdictText() {
      const door = orderStatus.formatDoorVerifyStatus(this.$t.bind(this), this.orderInfo.doorVerifyStatus);
      const parts = [];
      if (door && door !== "-") {
        parts.push(door);
      }
      const weight = orderStatus.formatSysVerifyWeight(this.$t.bind(this), this.orderInfo.sysVerifyWeight);
      if (weight && weight !== "-") {
        parts.push(this.$t("order.weightCheck") + ": " + weight);
      }
      return parts.length ? parts.join(" · ") : this.$t("order.reconcileGeneric");
    },
    settlementDeadline() {
      const created = this.parseTime(this.orderInfo.createTime);
      if (!created) {
        return 0;
      }
      return created + SETTLEMENT_WINDOW_HOURS * 3600 * 1000;
    },
    withinWindow() {
      if (!this.settlementDeadline || !this.nowTs) {
        return true;
      }
      return this.nowTs < this.settlementDeadline;
    },
    windowText() {
      if (!this.settlementDeadline || !this.nowTs) {
        return "";
      }
      if (!this.withinWindow) {
        return this.$t("order.windowClosed");
      }
      const remainMs = this.settlementDeadline - this.nowTs;
      const hours = Math.floor(remainMs / 3600000);
      const mins = Math.floor((remainMs % 3600000) / 60000);
      const label = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      return this.$t("order.windowOpen", { time: label });
    }
  },
  watch: {
    "orderInfo.id": {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.$nextTick(() => this.init());
        }
      }
    }
  },
  beforeDestroy() {
    this.stopWindowTimer();
  },
  methods: {
    money(v) {
      if (v === null || v === undefined || v === "") {
        return "$0.00";
      }
      const n = Number(v);
      return Number.isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
    },
    round(v) {
      return Math.round((Number(v) || 0) * 100) / 100;
    },
    parseTime(t) {
      if (!t) {
        return 0;
      }
      const ts = new Date(String(t).replace(/-/g, "/")).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    },
    isMismatch(item) {
      return item.detectedNum !== null && Number(item.detectedNum) !== Number(item.billedNum);
    },
    init() {
      this.form = { approvalStatus: "" };
      this.selectedProducts = [];
      this.videoList = [];
      this.videoPosterMap = {};
      this.seedLineItems();
      this.startWindowTimer();
      this.loadVideos();
    },
    startWindowTimer() {
      this.nowTs = new Date().getTime();
      this.stopWindowTimer();
      this.windowTimer = setInterval(() => {
        this.nowTs = new Date().getTime();
      }, 30000);
    },
    stopWindowTimer() {
      if (this.windowTimer) {
        clearInterval(this.windowTimer);
        this.windowTimer = null;
      }
    },
    // 用订单已有的 ordersGoods 预填对账行：qty 默认取「已计费」（现状），
    // 「已确认」仅作参考并高亮不一致 —— 避免误导性地自动建议退款。
    seedLineItems() {
      this.lineItems = (this.orderInfo.ordersGoods || []).map(g => {
        const billed = Number(g.buyNum) || 0;
        const detected =
          g.successNum === null || g.successNum === undefined || g.successNum === ""
            ? null
            : Number(g.successNum);
        const lineTotal = Number(g.payablePrice) || 0;
        const unit = Number(g.sellingPrice) || (billed > 0 ? lineTotal / billed : lineTotal) || 0;
        return {
          openDoorAisleGoodsId: g.openDoorAisleGoodsId || g.aisleGoodsId || null,
          goodsId: g.goodsId,
          goodsName: g.goodsName,
          url: g.goodsUrl || g.url,
          unitPrice: this.round(unit),
          billedNum: billed,
          detectedNum: detected,
          qty: billed
        };
      });
    },
    removeLine(index) {
      this.lineItems.splice(index, 1);
    },
    loadVideos() {
      if (!this.orderInfo.id) {
        return;
      }
      this.videoLoading = true;
      this.$api
        .request(this.$url.OpenDoor.getOrderVideoResources, this.$method.get, {
          ordersId: this.orderInfo.id
        })
        .then(res => {
          if (res.code === 0) {
            this.videoList = Array.isArray(res.data) ? res.data : [];
            this.prepareVideoPosters(this.videoList);
          }
          this.videoLoading = false;
        })
        .catch(() => {
          this.videoLoading = false;
        });
    },
    previewVideo(video) {
      this.previewResourceUrl = video.resourceUrl || "";
      this.videoPreviewVisible = true;
    },
    prepareVideoPosters(videoList) {
      videoList.forEach(video => {
        const resourceUrl = video.resourceUrl;
        if (!resourceUrl || this.videoPosterMap[resourceUrl]) {
          return;
        }
        this.generateVideoPoster(resourceUrl).then(posterUrl => {
          if (posterUrl) {
            this.$set(this.videoPosterMap, resourceUrl, posterUrl);
          }
        });
      });
    },
    generateVideoPoster(videoUrl) {
      return new Promise(resolve => {
        if (!videoUrl) {
          resolve("");
          return;
        }

        const video = document.createElement("video");
        let settled = false;
        const finalize = posterUrl => {
          if (settled) {
            return;
          }
          settled = true;
          video.pause();
          video.removeAttribute("src");
          video.load();
          resolve(posterUrl || "");
        };
        const captureFrame = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 320;
            canvas.height = video.videoHeight || 180;
            const context = canvas.getContext("2d");
            if (!context) {
              finalize("");
              return;
            }
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            finalize(canvas.toDataURL("image/jpeg", 0.82));
          } catch (error) {
            finalize("");
          }
        };

        video.crossOrigin = "anonymous";
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;
        video.onloadeddata = () => {
          const duration = Number(video.duration) || 0;
          if (duration > 0.12) {
            try {
              video.currentTime = Math.min(0.1, duration / 2);
            } catch (error) {
              captureFrame();
            }
            return;
          }
          captureFrame();
        };
        video.onseeked = captureFrame;
        video.onerror = () => finalize("");
        video.src = videoUrl;
        video.load();
        setTimeout(() => finalize(""), 5000);
      });
    },
    handleVideoThumbnailLoaded(event) {
      const player = event.target;
      const duration = Number(player.duration) || 0;
      if (duration <= 0 || player.dataset.thumbnailReady === "1") {
        return;
      }
      player.dataset.thumbnailReady = "1";
      try {
        player.currentTime = Math.min(0.1, duration / 2);
      } catch (error) {
        player.dataset.thumbnailReady = "0";
      }
    },
    loadProducts() {
      const userEquipmentId = this.orderInfo.equipmentId || this.orderInfo.userEquipmentId;
      if (!userEquipmentId) {
        this.productList = [];
        return;
      }
      this.productLoading = true;
      this.$api
        .request(this.$url.OpenDoor.getAisleGoodsConfig, this.$method.get, {
          userEquipmentId
        })
        .then(res => {
          if (res.code === 0) {
            this.productList = this.normalizeProductList(res.data);
          } else {
            this.$message.warning(res.msg);
          }
          this.productLoading = false;
        })
        .catch(() => {
          this.productLoading = false;
          this.$message.error(this.$t("order.loadProductsFailed"));
        });
    },
    normalizeProductList(data) {
      const flatList = [];
      const source = data || {};
      if (Array.isArray(source)) {
        source.forEach(item => flatList.push(item));
      } else {
        Object.keys(source).forEach(key => {
          const group = Array.isArray(source[key]) ? source[key] : [];
          group.forEach(item => flatList.push(item));
        });
      }
      const uniqueProducts = [];
      flatList.forEach(item => {
        const openDoorAisleGoodsId = item.id;
        if (!openDoorAisleGoodsId || uniqueProducts.some(product => product.openDoorAisleGoodsId === openDoorAisleGoodsId)) {
          return;
        }
        uniqueProducts.push({
          openDoorAisleGoodsId,
          goodsId: item.goodsId,
          goodsName: item.goodsName,
          url: item.url,
          sellingPrice: item.sellingPrice,
          buyNum: 1
        });
      });
      return uniqueProducts;
    },
    addProduct(product) {
      if (this.selectedProducts.some(item => item.openDoorAisleGoodsId === product.openDoorAisleGoodsId)) {
        return;
      }
      this.selectedProducts.push({
        ...product
      });
    },
    removeProduct(product) {
      this.selectedProducts = this.selectedProducts.filter(
        item => item.openDoorAisleGoodsId !== product.openDoorAisleGoodsId
      );
    },
    confirmProducts() {
      this.selectedProducts.forEach(p => {
        const existing = this.lineItems.find(li => li.openDoorAisleGoodsId && li.openDoorAisleGoodsId === p.openDoorAisleGoodsId);
        if (existing) {
          existing.qty = Number(existing.qty) + (Number(p.buyNum) || 1);
        } else {
          this.lineItems.push({
            openDoorAisleGoodsId: p.openDoorAisleGoodsId,
            goodsId: p.goodsId,
            goodsName: p.goodsName,
            url: p.url,
            unitPrice: this.round(Number(p.sellingPrice) || 0),
            billedNum: 0,
            detectedNum: null,
            qty: Number(p.buyNum) || 1
          });
        }
      });
      this.selectedProducts = [];
      this.showChooseProduct = false;
    },
    setSubmitting(v) {
      this.submitting = v;
      this.$emit("loading", v);
    },
    // 由父级（对话框页脚 / 抽屉页脚）通过 $refs 调用
    submit() {
      if (!this.form.approvalStatus) {
        this.$message.warning(this.$t("order.approvalStatusPlaceholder"));
        return;
      }

      const reviewedLines = this.lineItems.filter(item => Number(item.qty) > 0);
      if (this.shouldReviewProducts && reviewedLines.length === 0) {
        this.$message.warning(this.$t("order.selectReviewProductsRequired"));
        return;
      }

      const payload = {
        ordersNumber: this.orderInfo.orderNumber,
        approvalStatus: this.form.approvalStatus
      };

      if (this.shouldReviewProducts) {
        payload.productDTOS = reviewedLines.map(item => ({
          openDoorAisleGoodsId: item.openDoorAisleGoodsId,
          goodsId: item.goodsId,
          goodsName: item.goodsName,
          url: item.url,
          sellingPrice: item.unitPrice,
          buyNum: Number(item.qty)
        }));
      }

      // 结算意图（向后兼容：当前后端忽略未知字段）。金额实际移动由后端 approvalOrder 依据
      // productDTOS 执行；前端不自行触发退款，避免重复退款——显式退款仍走独立退款抽屉。
      payload.settlement = {
        action: this.settlementAction,
        originalAmount: this.originalCharge,
        correctedAmount: this.correctedTotal,
        delta: this.delta,
        withinWindow: this.withinWindow,
        reasonCode: this.reasonCode()
      };

      const doSubmit = () => {
        this.setSubmitting(true);
        this.$api
          .request(this.$url.OpenDoor.approvalOrder, this.$method.post, payload)
          .then(res => {
            if (res.code === 0) {
              this.$message.success(this.$t("order.handleAbnormalSuccess"));
              this.$emit("refresh");
            } else {
              this.$message.warning(res.msg);
            }
            this.setSubmitting(false);
          })
          .catch(() => {
            this.setSubmitting(false);
            this.$message.error(this.$t("order.handleAbnormalFailed"));
          });
      };

      if (this.settlementAction === "recharge") {
        this.$confirm(this.$t("order.rebillConfirm"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(doSubmit)
          .catch(() => {});
        return;
      }

      doSubmit();
    },
    reasonCode() {
      const status = Number(this.form.approvalStatus);
      if (status === 1) {
        return "malicious";
      }
      if (status === 3) {
        return "normal_settlement";
      }
      const door = Number(this.orderInfo.doorVerifyStatus);
      const map = {
        4: "unable_to_recognize",
        6: "intentional_occlusion",
        7: "sku_not_in_cabinet",
        8: "other_alarm",
        9: "foreign_object",
        10: "suspected_device_fault"
      };
      return map[door] || "settlement_error";
    }
  }
};
</script>

<style scoped lang="scss">
.reconcile-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dialog-title,
.section-block__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dialog-title {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}
.dialog-title i,
.section-block__title i {
  color: #8b93a6;
}

/* 头部 */
.recon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.recon-head__amt {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.recon-head__amt-val {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
  font-variant-numeric: tabular-nums;
}
.recon-head__order {
  font-family: var(--vtm-mono, monospace);
  font-size: 12.5px;
  color: #8b93a6;
}

/* 判定横幅 */
.verdict-banner {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 11px 13px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid transparent;
}
.verdict-banner--crit {
  background: #fbe7ea;
  border-color: rgba(199, 35, 63, 0.28);
  color: #5c1420;
}
.verdict-banner--warn {
  background: #fbeedc;
  border-color: rgba(181, 71, 8, 0.28);
  color: #5c3410;
}
.verdict-banner__ic {
  font-size: 16px;
  margin-top: 1px;
}
.verdict-banner--crit .verdict-banner__ic {
  color: #c7233f;
}
.verdict-banner--warn .verdict-banner__ic {
  color: #b54708;
}
.verdict-banner__window {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.verdict-banner__window.is-open {
  color: #12805c;
}
.verdict-banner__window.is-closed {
  color: #b54708;
}

.section-block {
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
}
.section-block--compact {
  padding-bottom: 14px;
}
.section-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.section-block__title {
  color: #303133;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 12px;
}
.section-block__header .section-block__title {
  margin-bottom: 0;
}

/* 视频 */
.video-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 116px;
}
.video-item {
  position: relative;
  width: calc(33.333% - 8px);
  height: 116px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  background: #000;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.video-item:hover {
  border-color: #d8dee8;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.08);
}
.video-item__poster,
.video-item__player {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.video-item__player {
  display: block;
  pointer-events: none;
  background: #000;
}
.video-item__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #fff;
  background: linear-gradient(180deg, rgba(14, 18, 26, 0.14), rgba(14, 18, 26, 0.3));
}

/* 对账行 */
.recon-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.recon-line {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: var(--vtm-surface);
}
.recon-line__thumb {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
}
.recon-line__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.recon-line__main {
  flex: 1;
  min-width: 0;
}
.recon-line__name {
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.recon-line__meta {
  font-size: 11.5px;
  color: #909399;
  margin-top: 2px;
}
.recon-line__meta.is-mismatch {
  color: #b54708;
  font-weight: 600;
}
.recon-line__qty {
  width: 108px;
  flex: 0 0 108px;
}
.recon-line__price {
  width: 68px;
  flex: 0 0 68px;
  text-align: right;
  font-family: var(--vtm-mono, monospace);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: #303133;
}
.recon-line__rm {
  color: #c0c4cc;
  cursor: pointer;
  font-size: 15px;
}
.recon-line__rm:hover {
  color: #c7233f;
}

/* 重算 */
.recompute {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 13px 16px;
  background: #f1f2f4;
  border-radius: 6px;
  font-size: 13px;
}
.recompute__line {
  display: flex;
  justify-content: space-between;
  color: #486581;
}
.recompute__line--total {
  border-top: 1px dashed #d9dde3;
  padding-top: 7px;
  margin-top: 2px;
  font-weight: 600;
  color: #303133;
}
.recompute__num {
  font-family: var(--vtm-mono, monospace);
  font-variant-numeric: tabular-nums;
}
.recompute__num.is-down {
  color: #12805c;
}
.recompute__num.is-up {
  color: #b54708;
}
.recompute__note {
  font-size: 12px;
  color: #829ab1;
  margin-top: 2px;
}
.recompute__note--warn {
  color: #b54708;
}

/* 选品器 */
.product-selector {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.product-selector__available {
  max-height: 360px;
  padding: 8px 0 4px;
  overflow-y: auto;
}
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  align-items: start;
}
.product-grid__item {
  position: relative;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  background: var(--vtm-surface);
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.product-grid__item:hover {
  border-color: #d8dee8;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.08);
}
.product-grid__image {
  width: 136px;
  height: 136px;
  margin: 0 auto 10px;
}
.product-grid__image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.product-grid__name {
  min-height: 36px;
  overflow: hidden;
  color: #303133;
  line-height: 18px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.product-grid__price {
  margin: 6px 0 10px;
  color: #f56c6c;
  font-weight: 600;
}
.product-selector__selected {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 180px;
  align-items: flex-start;
  align-content: flex-start;
  padding: 8px 0 4px;
}
.product-selector__selected--empty {
  justify-content: center;
  align-items: center;
  align-content: center;
}
.product-grid__item--selected {
  width: 180px;
  cursor: default;
}
.product-grid__item--selected:hover {
  border-color: #dcdfe6;
  box-shadow: none;
}
.product-grid__remove {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  color: #909399;
}

::v-deep .reconcile-panel__choose .el-dialog__body {
  padding: 18px 20px;
}
::v-deep .el-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  margin: 0 auto;
}
</style>
